// Regex & Heuristic Input Type Inference Engine for CNC PM Templates per ORIGINAL_REQUEST § R2 & PROJECT.md
import type { InputType, CriticalLimit } from '../../types';

export interface GaugeInferenceResult {
  unit?: string;
  minValue?: number;
  maxValue?: number;
  criticalLimit?: CriticalLimit;
}

/**
 * Infer InputType from combined task description, criteria, method, and layout strings
 */
export function inferInputTypeFromText(
  taskText: string = '',
  criteriaText: string = '',
  methodText: string = '',
  layoutText: string = ''
): InputType {
  const combined = `${taskText} ${criteriaText} ${methodText} ${layoutText}`.toLowerCase();

  // 1. Numeric Gauge: Pressure gauges, units, ranges, or numeric measurement keywords
  if (
    /mpa|bar|°c|\bpsi\b|เกจ|แรงดัน|ความดัน|\bgauge\b|\bpressure\b|\bvolt\b|\bamp\b|\.\.\.\s*mpa/i.test(
      combined
    )
  ) {
    return 'numeric_gauge';
  }

  // 2. Select Option / Radio: Multi-option indicators, status alternatives, or option slashes
  if (
    /ปกติ|ชำรุด|เติมเพิ่ม|รั่วซึม|อุดตัน|มีฟิล์มน้ำมัน|แห้งผิดปกติ|เปลี่ยนแล้ว|ดำเนินการต่อ|ไม่มีอุปกรณ์|ตรวจสอบแล้ว|เปลี่ยนใหม่/i.test(
      combined
    ) ||
    (layoutText.includes('⬜') && (layoutText.match(/⬜/g) || []).length >= 2) ||
    layoutText.includes('/')
  ) {
    return 'select_option';
  }

  // 3. Single Action Checkbox: Single completion / confirmation actions
  if (
    /ทำแล้ว|ระบายแล้ว|ถ่ายแล้ว|อัดจาระบีแล้ว|เรียบร้อย|\bdone\b|\bchecked\b|\bcomplete\b/i.test(
      combined
    ) ||
    (layoutText.includes('⬜') && (layoutText.match(/⬜/g) || []).length === 1)
  ) {
    return 'done_checkbox';
  }

  return 'text';
}

/**
 * Extract numerical gauge unit, tolerance thresholds, and critical limits from text
 */
export function extractGaugeBounds(
  taskText: string = '',
  criteriaText: string = '',
  methodText: string = '',
  limitsText: string = ''
): GaugeInferenceResult {
  const combined = `${taskText} ${criteriaText} ${methodText} ${limitsText}`;
  const result: GaugeInferenceResult = {};

  // Unit detection
  const unitMatch = combined.match(/\b(MPa|bar|psi|°C|%|mm|kg|L|V|A)\b/i);
  if (unitMatch) {
    result.unit = unitMatch[1];
  } else if (/เกจ|แรงดัน/i.test(combined)) {
    result.unit = 'MPa';
  }

  // 1. Hydraulic pressure special safety rule: >= 6.8 MPa
  if (/6\.8/i.test(combined) && (/ไฮดรอลิก|hydraulic/i.test(combined) || /mpa/i.test(combined))) {
    result.minValue = 6.8;
    result.unit = 'MPa';
    result.criticalLimit = {
      operator: '<',
      value: 6.8,
      warningMessage: 'แรงดันระบบไฮดรอลิกต่ำกว่าเกณฑ์ความปลอดภัย 6.8 MPa (68 bar)',
    };
    return result;
  }

  // 2. Range: e.g. "2.5 - 3.5 MPa" or "2.5 ถึง 3.5" or "70-90%"
  const rangeMatch = combined.match(/(\d+(?:\.\d+)?)\s*(?:-|–|ถึง|to)\s*(\d+(?:\.\d+)?)/i);
  if (rangeMatch) {
    const minVal = parseFloat(rangeMatch[1]);
    const maxVal = parseFloat(rangeMatch[2]);
    if (!isNaN(minVal) && !isNaN(maxVal)) {
      result.minValue = minVal;
      result.maxValue = maxVal;
      return result;
    }
  }

  // 3. Minimum threshold: e.g. ">= 6.8" or "ไม่ต่ำกว่า 6.8" or "Min 6.8"
  const minMatch = combined.match(/(?:>=|不低于|ไม่ต่ำกว่า|min(?:imum)?)\s*[:=]?\s*(\d+(?:\.\d+)?)/i);
  if (minMatch) {
    const val = parseFloat(minMatch[1]);
    if (!isNaN(val)) {
      result.minValue = val;
      result.criticalLimit = {
        operator: '<',
        value: val,
        warningMessage: `ค่าที่วัดได้ต่ำกว่าเกณฑ์ขั้นต่ำ ${val} ${result.unit || ''}`,
      };
      return result;
    }
  }

  // 4. Maximum threshold: e.g. "<= 10" or "ไม่เกิน 10" or "Max 10"
  const maxMatch = combined.match(/(?:<=|ไม่เกิน|สูงสุด|max(?:imum)?)\s*[:=]?\s*(\d+(?:\.\d+)?)/i);
  if (maxMatch) {
    const val = parseFloat(maxMatch[1]);
    if (!isNaN(val)) {
      result.maxValue = val;
      result.criticalLimit = {
        operator: '>',
        value: val,
        warningMessage: `ค่าที่วัดได้สูงกว่าเกณฑ์สูงสุด ${val} ${result.unit || ''}`,
      };
      return result;
    }
  }

  // 5. Nominal target: e.g. "ประมาณ 0.8 MPa" or "~0.8 MPa"
  const nominalMatch = combined.match(/(?:ประมาณ|~|nominal)\s*(\d+(?:\.\d+)?)/i);
  if (nominalMatch) {
    const val = parseFloat(nominalMatch[1]);
    if (!isNaN(val)) {
      // Default tolerance ± 20%
      const tol = val * 0.2;
      result.minValue = Math.round((val - tol) * 100) / 100;
      result.maxValue = Math.round((val + tol) * 100) / 100;
    }
  }

  return result;
}

/**
 * Extract select options array from layout text or criteria
 */
export function extractSelectOptions(layoutText: string = '', criteriaText: string = ''): string[] {
  const combined = `${layoutText} ${criteriaText}`;

  // Check known specific pairs
  if (/มีฟิล์มน้ำมัน/i.test(combined) && /แห้งผิดปกติ/i.test(combined)) {
    return ['มีฟิล์มน้ำมัน', 'แห้งผิดปกติ'];
  }
  if (/เปลี่ยนแล้ว/i.test(combined) && /ไม่มีอุปกรณ์/i.test(combined)) {
    return ['เปลี่ยนแล้ว', 'ไม่มีอุปกรณ์นี้'];
  }
  if (/เปลี่ยนแล้ว/i.test(combined) && /ดำเนินการต่อ/i.test(combined)) {
    return ['เปลี่ยนแล้ว', 'ดำเนินการต่อ'];
  }
  if (/ตรวจสอบแล้ว/i.test(combined) && /เปลี่ยนใหม่/i.test(combined)) {
    return ['ตรวจสอบแล้ว', 'เปลี่ยนใหม่'];
  }
  if (/ตรวจสอบแรงดัน/i.test(combined) && /เปลี่ยนใหม่/i.test(combined)) {
    return ['ตรวจสอบแรงดัน', 'เปลี่ยนใหม่'];
  }
  if (/ปกติ/i.test(combined) && /เติมเพิ่ม/i.test(combined)) {
    return ['ปกติ', 'เติมเพิ่ม'];
  }
  if (/ปกติ/i.test(combined) && /รั่วซึม/i.test(combined)) {
    return ['ปกติ', 'รั่วซึม'];
  }
  if (/ปกติ/i.test(combined) && /อุดตัน/i.test(combined)) {
    return ['ปกติ', 'อุดตัน'];
  }
  if (/ปกติ/i.test(combined) && /อัดจาระบีแล้ว/i.test(combined)) {
    return ['ปกติ', 'อัดจาระบีแล้ว'];
  }
  if (/ปกติ/i.test(combined) && /ชำรุด/i.test(combined)) {
    return ['ปกติ', 'ชำรุด'];
  }

  // Parse by checkbox delimiter "⬜"
  if (layoutText.includes('⬜')) {
    const parts = layoutText
      .split('⬜')
      .map((s) => s.replace(/\(Done\)/gi, '').trim())
      .filter((s) => s.length > 0 && !/^[\s.,:;]+$/.test(s));
    if (parts.length >= 2) {
      return parts;
    }
  }

  // Parse by slash delimiter "/"
  if (layoutText.includes('/')) {
    const parts = layoutText
      .split('/')
      .map((s) => s.replace(/⬜|\(Done\)/gi, '').trim())
      .filter((s) => s.length > 0);
    if (parts.length >= 2) {
      return parts;
    }
  }

  // Default fallback options
  return ['ปกติ', 'ชำรุด'];
}

/**
 * Extract operating hours milestone number from task text
 * e.g. "[ทุก 500 ชม.]" -> 500, "[ทุก 1,000 ชม.]" -> 1000
 */
export function extractHoursThreshold(
  taskText: string = '',
  criteriaText: string = ''
): number | undefined {
  const fullText = `${taskText} ${criteriaText}`;
  const match = fullText.match(/(?:ทุก|every)?\s*([0-9,]+)\s*(?:ชม\.?|hours?|\bh\b)/i);
  if (match) {
    const cleanNum = parseInt(match[1].replace(/,/g, ''), 10);
    if (!isNaN(cleanNum) && cleanNum > 0) {
      return cleanNum;
    }
  }
  return undefined;
}
